import { Link } from 'react-router';
import { Hero } from '@/components/sections/Hero';
import { CtaBlock } from '@/components/sections/CtaBlock';
import { SectionHeading } from '@/components/ui/SectionHeading';
import { services } from '@/data/services';
import { howToChoose, whatWeDoNotDo } from '@/data/studio';
import { Branding, DesignLayout, CodeLaptop } from '@/illustrations';
import { ArrowUpRight } from '@/icons';
import { useSeo } from '@/lib/seo';

const illustrationMap = {
  branding: Branding,
  designLayout: DesignLayout,
  codeLaptop: CodeLaptop,
};

export default function ServicesOverview() {
  useSeo({
    title: 'Services',
    description: 'Logo design, website design, and website development - each offered with the same standard of craft.',
  });

  return (
    <>
      <Hero
        eyebrow="Services"
        headline={<>Three services. One standard.</>}
        description="We ship logos, marketing sites, and production-grade code. Pick one or combine all three."
        align="centered"
      />

      <section className="px-6 md:px-12 lg:px-20 py-16 md:py-20 border-t border-border">
        <div className="mx-auto max-w-[1440px] grid lg:grid-cols-[0.8fr_1.2fr] gap-10 lg:gap-16">
          <SectionHeading eyebrow="Not sure which?" title="How to pick." description="A short decision helper. If any of these fit you, start there." />
          <dl className="flex flex-col">
            {howToChoose.map((row) => (
              <div key={row.condition} className="grid md:grid-cols-[1fr_auto] gap-4 md:gap-10 py-4 border-b border-border">
                <dt className="text-ink-primary">{row.condition}</dt>
                <dd className="mono-meta text-accent md:text-right">{row.recommendation}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 pb-24 md:pb-32">
        <div className="mx-auto max-w-[1440px] flex flex-col">
          {services.map((s, index) => {
            const Illustration = illustrationMap[s.illustration];
            const reverse = index % 2 === 1;
            return (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                className="group grid lg:grid-cols-2 gap-10 lg:gap-20 items-center py-16 md:py-20 border-b border-border last:border-b-0 transition-colors hover:text-accent"
              >
                <div className={reverse ? 'lg:order-2' : ''}>
                  <span className="mono-meta text-ink-muted">0{index + 1}</span>
                  <h2 className="display-lg mt-4 leading-none">{s.title}</h2>
                  <p className="text-lg text-ink-secondary mt-6 max-w-xl leading-relaxed group-hover:text-ink-primary transition-colors">
                    {s.tagline}
                  </p>
                  <span className="inline-flex items-center gap-2 mt-8 font-sans text-accent">
                    Read more <ArrowUpRight className="w-5 h-5" />
                  </span>
                </div>
                <div className={reverse ? 'lg:order-1' : ''}>
                  <Illustration className="w-full max-w-lg text-ink-primary transition-transform duration-500 group-hover:-translate-y-2" />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-20 md:py-24 bg-surface border-t border-border">
        <div className="mx-auto max-w-[1440px] grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-20">
          <SectionHeading
            eyebrow="Out of scope"
            title={<>What we do not do.</>}
            description="To save everyone time: here is what we will not take on, even for money. If your need is on this list, we can probably recommend someone."
          />
          <ul className="flex flex-col">
            {whatWeDoNotDo.map((item) => (
              <li key={item} className="text-ink-muted leading-relaxed py-3 border-b border-border">
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <CtaBlock />
    </>
  );
}
